import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { PublicHeader } from "@/components/PublicHeader";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PrivacyNote } from "@/components/PrivacyNote";
import { useEjercicio } from "@/context/EjercicioContext";
import { api } from "@/lib/api";

export default function TuPaso() {
  const navigate = useNavigate();
  const { data, update } = useEjercicio();
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(!!data.leadId);

  const noStep = !data.primer_paso?.trim();

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!nombre.trim()) errs.nombre = "Completa este paso para continuar.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()))
      errs.email = "Revisa el email antes de continuar.";
    if (!consent) errs.consent = "Necesitamos tu permiso para guardar estos datos.";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    const res = await api.crearLead({
      nombre: nombre.trim(),
      email: email.trim(),
      cambio: data.cambio,
      importancia: data.importancia,
      primer_paso: data.primer_paso,
      consentimiento: true,
    });
    setSaving(false);
    if (res.ok) {
      update({ leadId: res.data.id });
      setDone(true);
    } else {
      setErrors({ general: res.error });
    }
  };

  if (noStep && !done) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <main className="wrap-narrow py-20 text-center">
          <h1 className="font-display text-3xl text-[#102B5B]" style={{ fontWeight: 600 }}>
            Primero define tu paso
          </h1>
          <p className="mt-4 text-lg text-[#343A46]">
            Empieza poniendo nombre a ese cambio y elige una acción concreta.
          </p>
          <Link
            to="/claridad"
            className="mt-7 inline-block rounded-full bg-[#275FBE] px-7 py-3.5 font-semibold text-white"
            data-testid="tupaso-go-claridad"
          >
            Ir al ejercicio
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      <main className="wrap-narrow py-10 sm:py-14">
        <p className="eyebrow mb-4">Tu primer paso</p>
        <h1
          className="font-display text-4xl text-[#102B5B] sm:text-5xl"
          style={{ fontWeight: 600 }}
          data-testid="tupaso-title"
        >
          Este es tu primer paso.
        </h1>

        {/* Paso destacado */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-8 rounded-3xl bg-[#102B5B] p-7 sm:p-9"
        >
          <p className="text-sm font-semibold uppercase tracking-wide text-[#C5D7F4]">
            El paso que depende de ti
          </p>
          <p
            className="font-display mt-3 text-2xl leading-snug text-white sm:text-3xl"
            style={{ fontWeight: 600 }}
            data-testid="tupaso-paso"
          >
            {data.primer_paso}
          </p>
          <p className="mt-5 text-base text-[#E6EEFA]">
            No necesitas sentirte completamente segura para empezar.
          </p>
        </motion.div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10 soft-card p-8 text-center"
            data-testid="tupaso-success"
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#1E6A45]">
              <Check className="text-white" size={28} />
            </span>
            <h2 className="font-display mt-5 text-2xl text-[#102B5B]" style={{ fontWeight: 600 }}>
              Ya has dejado de pensarlo solo en tu cabeza.
            </h2>
            <p className="mt-3 text-lg text-[#343A46]">
              Tienes un primer paso concreto. Ahora puedes empezar a moverlo.
            </p>
            <button
              type="button"
              onClick={() => navigate("/sesion")}
              data-testid="tupaso-reservar"
              className="mt-7 rounded-full bg-[#275FBE] px-8 py-3.5 text-base font-semibold text-white transition-transform duration-150 hover:scale-[1.03] active:scale-95"
            >
              Reservar sesión
            </button>
            <div className="mt-4">
              <Link to="/area" className="text-base font-semibold text-[#1F4C98] hover:underline">
                Entrar en mi espacio
              </Link>
            </div>
          </motion.div>
        ) : (
          <form onSubmit={submit} noValidate className="mt-10 soft-card p-7 sm:p-9" data-testid="tupaso-form">
            <div className="space-y-5">
              <div>
                <label htmlFor="nombre" className="text-base font-semibold text-[#171A20]">
                  Nombre
                </label>
                <Input
                  id="nombre"
                  data-testid="tupaso-nombre"
                  value={nombre}
                  maxLength={80}
                  onChange={(e) => setNombre(e.target.value)}
                  className="mt-2 rounded-xl border-[#D6DAE0] text-base focus-visible:ring-[#275FBE]"
                  aria-invalid={!!errors.nombre}
                />
                {errors.nombre && <p className="field-error mt-1.5">⚠ {errors.nombre}</p>}
              </div>
              <div>
                <label htmlFor="email" className="text-base font-semibold text-[#171A20]">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  data-testid="tupaso-email"
                  value={email}
                  maxLength={160}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 rounded-xl border-[#D6DAE0] text-base focus-visible:ring-[#275FBE]"
                  aria-invalid={!!errors.email}
                />
                {errors.email && <p className="field-error mt-1.5">⚠ {errors.email}</p>}
              </div>
              <label className="flex items-start gap-3">
                <Checkbox
                  checked={consent}
                  onCheckedChange={(v) => setConsent(!!v)}
                  data-testid="tupaso-consent"
                  className="mt-1 border-[#9299A4] data-[state=checked]:bg-[#275FBE] data-[state=checked]:border-[#275FBE]"
                />
                <span className="text-base text-[#343A46]">
                  Doy mi permiso para guardar estos datos y recibir mi ejercicio.
                </span>
              </label>
              {errors.consent && <p className="field-error">⚠ {errors.consent}</p>}
              <PrivacyNote />
              {errors.general && (
                <p className="field-error" data-testid="tupaso-general-error">
                  ⚠ {errors.general}
                </p>
              )}
              <button
                type="submit"
                disabled={saving}
                data-testid="tupaso-guardar"
                className="w-full rounded-full bg-[#275FBE] py-4 text-base font-semibold text-white transition-transform duration-150 hover:scale-[1.01] active:scale-95 disabled:opacity-70"
              >
                {saving ? "Guardando…" : "Guardar mi paso"}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
